// frontend/src/modules/payments/components/RefundDialog.tsx
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { formatMoney } from '../../../design-system/format';
import { paymentsApi } from '../api';
import type { Payment } from '../types';

const REASONS = ['requested_by_customer', 'duplicate', 'fraudulent'] as const;
const REASON_LABELS: Record<string, string> = {
  requested_by_customer: 'Requested by customer',
  duplicate: 'Duplicate charge',
  fraudulent: 'Fraudulent',
};

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  payment: Payment;
  /**
   * Sum already refunded (pending + succeeded) against this payment, when the
   * caller has the refund list loaded. Null/undefined → unknown, so the max
   * falls back to the gross charge and is labeled as such (the backend still
   * validates against the true net-refundable balance).
   */
  alreadyRefunded?: number | null;
  onRefunded: () => void;
}

/**
 * Issue a full or partial refund against a captured Stripe payment. The backend
 * validates the amount against the net-refundable balance and reconciles the
 * related invoice's settlement state on success.
 */
export function RefundDialog({ open, onOpenChange, payment, alreadyRefunded, onRefunded }: Props) {
  const gross = Number(payment.amount);
  const netKnown = alreadyRefunded != null;
  const max = netKnown ? Math.max(0, gross - (alreadyRefunded as number)) : gross;
  const currency = payment.currency.toUpperCase();
  const [mode, setMode] = React.useState<'full' | 'partial'>('full');
  const [amount, setAmount] = React.useState('');
  const [reason, setReason] = React.useState<string>('requested_by_customer');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setMode('full');
      setAmount(String(max));
      setReason('requested_by_customer');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    let amt: number | null = null;
    if (mode === 'partial') {
      amt = Number(amount);
      if (!(amt > 0)) { toast.error('Enter a positive amount'); return; }
      if (amt > max + 0.005) { toast.error(`Cannot exceed ${formatMoney(max, currency)}`); return; }
    }
    setBusy(true);
    try {
      const refund = await paymentsApi.initiateRefund({
        invoice_id: payment.invoice_id,
        payment_id: payment.id,
        amount: amt,
        reason,
      });
      toast.success(
        refund.status === 'succeeded'
          ? 'Refund issued'
          : refund.status === 'pending'
            ? 'Refund submitted — pending with Stripe'
            : 'Refund recorded',
      );
      onRefunded();
      onOpenChange(false);
    } catch (err: any) {
      toast.error('Could not issue refund', { description: err.message ?? String(err) });
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Refund payment</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4 pt-2">
          <p className="text-[13px] text-muted-foreground">
            Refunding a payment of <span className="font-mono font-semibold text-foreground">{formatMoney(gross, currency)}</span>.
            {netKnown && (alreadyRefunded as number) > 0 && (
              <> <span className="font-mono font-semibold text-foreground">{formatMoney(alreadyRefunded as number, currency)}</span> has already been refunded,
              leaving <span className="font-mono font-semibold text-foreground">{formatMoney(max, currency)}</span> refundable.</>
            )}
            {' '}A full refund moves the invoice back to unpaid; a partial refund leaves it partially paid.
          </p>
          <div className="grid gap-1.5">
            <Label>Refund</Label>
            <Select value={mode} onValueChange={(v) => setMode(v as 'full' | 'partial')}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="full">
                  {netKnown ? `Remaining balance (${formatMoney(max, currency)})` : `Full amount (${formatMoney(max, currency)})`}
                </SelectItem>
                <SelectItem value="partial">Partial amount</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {mode === 'partial' && (
            <div className="grid gap-1.5">
              <Label>Amount<span className="text-destructive ml-0.5">*</span></Label>
              <Input
                type="number" min="0.01" max={max} step="0.01"
                value={amount} onChange={(e) => setAmount(e.target.value)} required autoFocus
              />
              <p className="text-xs text-muted-foreground">
                {netKnown
                  ? `Up to ${formatMoney(max, currency)} refundable.`
                  : `Up to the original charge (${formatMoney(max, currency)}) — Stripe rejects amounts above what's still refundable.`}
              </p>
            </div>
          )}
          <div className="grid gap-1.5">
            <Label>Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {REASONS.map((r) => <SelectItem key={r} value={r}>{REASON_LABELS[r]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" variant="destructive" disabled={busy}>
              {busy ? 'Refunding…' : 'Issue refund'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
